import React, { useEffect, useMemo, useRef, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Sortable from "sortablejs"
import { getRequest, patchRequest, postRequest } from "@react/helpers/fetch"
import Modal         from "@react/shared/ui/Modal"
import Button        from "@react/shared/ui/Button"
import IconButton    from "@react/shared/ui/IconButton"
import ProductPicker from "@react/shared/ui/ProductPicker"
import QuillInput    from "@react/shared/quillInput"
import Combobox      from "@shared/combobox"
import MarginBadge   from "../qline_service_blueprints/components/MarginBadge"
import { COST_CATEGORIES } from "../form/qlines/shared/category_styles"

// Line editor for an Ouvrage (quotes_qline_group). Mirrors the Prestations
// BlueprintFormModal look, adapted to the group data model: no cost category,
// but supports regular lines, product-linked lines, free-text descriptive
// lines and page breaks, plus the per-line is_option / hide_in_pdf flags.
// handle | flex spacer | unit | qty | purchase | sale | coef | margin | total | options | remove
const GRID = "28px 1fr 88px 76px 104px 104px 72px 80px 116px 34px 34px"
const BD_GRID = "minmax(120px,1.2fr) minmax(120px,1fr) 70px 76px 100px 100px 66px 74px 104px 34px"

const t = (key, opts) => I18n.t(`qline_groups.form.${key}`, opts)

const rid = () => Math.random().toString(36).slice(2, 10)

const blankBreakdown = (cost_category = "labor") => ({
  _clientId:           rid(),
  id:                  null,
  cost_category,
  description:         "",
  ref_product_distrib: "",
  unit:                cost_category === "labor" ? "h" : "u",
  quantity:            1,
  unit_price:          0,
  purchase_price:      0,
  product_id:          null,
  product_name:        "",
  _destroy:            false,
})

const breakdownRollup = breakdowns => {
  let total = 0, cost = 0
  ;(breakdowns || []).filter(b => !b._destroy).forEach(b => {
    const q = parseFloat(b.quantity) || 0
    total += q * (parseFloat(b.unit_price) || 0)
    cost  += q * (parseFloat(b.purchase_price) || 0)
  })
  const margin = total - cost
  return { total, cost, margin, coef: cost > 0 ? total / cost : null, marginPct: total > 0 ? (margin / total) * 100 : 0 }
}

const blankLine = (kind = "regular") => ({
  _clientId:           rid(),
  id:                  null,
  kind,
  description:         "",
  ref_product_distrib: "",
  unit:                kind === "regular" ? "u" : "",
  quantity:            kind === "regular" ? 1 : 0,
  unit_price:          0,
  purchase_price:      0,
  is_option:           false,
  hide_in_pdf:         false,
  product_id:          null,
  product_name:        "",
  breakdowns:          kind === "prestation" ? [blankBreakdown()] : [],
  _destroy:            false,
})

const kindOf = line => {
  if (line.is_page_break) return "page_break"
  if ((line.qline_breakdown_blueprints || []).length > 0) return "prestation"
  const qty  = parseFloat(line.quantity)   || 0
  const sale = parseFloat(line.unit_price)  || 0
  if (line.description && qty === 0 && sale === 0) return "descriptive"
  return "regular"
}

const normalize = line => ({
  _clientId:           rid(),
  id:                  line.id,
  kind:                kindOf(line),
  description:         line.description || "",
  ref_product_distrib: line.ref_product_distrib || "",
  unit:                line.unit || "u",
  quantity:            line.quantity || 0,
  unit_price:          line.unit_price || 0,
  purchase_price:      line.purchase_price || 0,
  is_option:           !!line.is_option,
  hide_in_pdf:         !!line.hide_in_pdf,
  product_id:          line.product?.id || null,
  product_name:        line.product?.name || "",
  breakdowns:          (line.qline_breakdown_blueprints || []).map(b => ({
    _clientId:           rid(),
    id:                  b.id,
    cost_category:       b.cost_category || "materials",
    description:         b.description || "",
    ref_product_distrib: b.ref_product_distrib || "",
    unit:                b.unit || (b.cost_category === "labor" ? "h" : "u"),
    quantity:            b.quantity || 0,
    unit_price:          b.unit_price || 0,
    purchase_price:      b.purchase_price || 0,
    product_id:          b.product?.id || null,
    product_name:        b.product?.name || "",
    _destroy:            false,
  })),
  _pickMode:           false,
  _destroy:            false,
})

const money = amount =>
  amount == null || isNaN(amount)
    ? "—"
    : `${amount.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`

const coefFmt = coef =>
  coef == null ? "—" : `×${coef.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

// ── Per-line options popover (is_option / hide_in_pdf) ──────────────────────
const LineOptions = ({ line, onChange }) => {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    if (!open) return
    const onDoc = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener("mousedown", onDoc)
    return () => document.removeEventListener("mousedown", onDoc)
  }, [open])

  const active = line.is_option || line.hide_in_pdf

  return (
    <div ref={ref} className="relative flex items-center justify-center">
      <button
        type      = "button"
        onClick   = {() => setOpen(o => !o)}
        title     = {t("line_options")}
        className = {`w-8 h-8 grid place-items-center rounded-md hover:bg-slate-100 ${active ? "text-brand-600" : "text-slate-400"}`}
      >
        <FontAwesomeIcon icon="ellipsis-vertical" />
      </button>
      {open && (
        <div className="absolute right-0 top-9 z-30 w-56 rounded-lg border border-slate-200 bg-white shadow-lg p-2 text-sm text-left">
          <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer">
            <input type="checkbox" checked={line.is_option} onChange={e => onChange({ is_option: e.target.checked })}
                   className="h-4 w-4 rounded border-slate-300 text-brand-500 focus:ring-brand-200" />
            <span className="text-slate-700">{t("is_option")}</span>
          </label>
          <label className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer">
            <input type="checkbox" checked={line.hide_in_pdf} onChange={e => onChange({ hide_in_pdf: e.target.checked })}
                   className="h-4 w-4 rounded border-slate-300 text-brand-500 focus:ring-brand-200" />
            <span className="text-slate-700">{t("hide_in_pdf")}</span>
          </label>
        </div>
      )}
    </div>
  )
}

const CELL = "w-full h-9 px-2 rounded border border-transparent hover:border-slate-200 focus:border-brand-500 focus:bg-white focus:outline-none text-sm"

const QlineGroupFormModal = ({ isOpen, mode, groupId, showUrl, saveUrl, updateUrl, qlineServiceBlueprintOptions, onClose, onSaved }) => {
  const [loading, setLoading] = useState(false)
  const [saving,  setSaving]  = useState(false)

  const [title,        setTitle]        = useState("")
  const [unit,         setUnit]         = useState("")
  const [observation,  setObservation]  = useState("")
  const [subtotal,     setSubtotal]     = useState(false)
  const [titleAsLabel, setTitleAsLabel] = useState(false)
  const [lines,        setLines]        = useState([blankLine()])
  const obsRef = useRef(null)

  useEffect(() => {
    if (!obsRef.current) return
    obsRef.current.style.height = "auto"
    obsRef.current.style.height = `${obsRef.current.scrollHeight}px`
  }, [observation, isOpen])

  useEffect(() => {
    if (!isOpen) return
    if (mode === "create") {
      setTitle(""); setUnit(""); setObservation(""); setSubtotal(false); setTitleAsLabel(false)
      setLines([blankLine()])
      return
    }
    if (groupId && showUrl) {
      setLoading(true)
      getRequest(showUrl)
        .then(data => {
          setTitle(data.title || "")
          setUnit(data.unit || "")
          setObservation(data.observation || "")
          setSubtotal(!!data.include_subtotal)
          setTitleAsLabel(!!data.title_as_subtotal_label)
          const mapped = (data.lines || []).map(normalize)
          setLines(mapped.length ? mapped : [blankLine()])
        })
        .finally(() => setLoading(false))
    }
  }, [isOpen, mode, groupId, showUrl])

  const visible = lines.filter(l => !l._destroy)

  const rollup = useMemo(() => {
    let total = 0, cost = 0
    visible.forEach(line => {
      if (line.kind === "prestation") {
        const r = breakdownRollup(line.breakdowns)
        total += r.total
        cost  += r.cost
        return
      }
      if (line.kind !== "regular") return
      const qty = parseFloat(line.quantity) || 0
      total += qty * (parseFloat(line.unit_price) || 0)
      cost  += qty * (parseFloat(line.purchase_price) || 0)
    })
    const margin = total - cost
    return {
      total, cost, margin,
      coef:      cost > 0  ? total / cost           : null,
      marginPct: total > 0 ? (margin / total) * 100 : 0,
    }
  }, [visible])

  const update = (clientId, patch) =>
    setLines(prev => prev.map(l => (l._clientId === clientId ? { ...l, ...patch } : l)))

  const remove = clientId =>
    setLines(prev => prev.map(l => {
      if (l._clientId !== clientId) return l
      return l.id ? { ...l, _destroy: true } : null
    }).filter(Boolean))

  const prestationOptions = qlineServiceBlueprintOptions || []

  const addLine = (kind = "regular") => setLines(prev => {
    if (kind === "prestation") {
      // With saved prestations available, a fresh line starts in "pick" mode
      // (choose an existing one or build manually), mirroring the quote form.
      const hasOptions = prestationOptions.length > 0
      return [...prev, { ...blankLine("prestation"), breakdowns: hasOptions ? [] : [blankBreakdown()], _pickMode: hasOptions }]
    }
    return [...prev, blankLine(kind)]
  })

  // Seed a prestation line's breakdown from a saved Prestation
  // (QlineServiceBlueprint) — copies its lines as new rows.
  const seedFromPrestation = (clientId, id) => {
    getRequest(`/quotes/qline_service_blueprints/${id}.json`).then(data => update(clientId, {
      _pickMode:           false,
      description:         data.name || "",
      unit:                data.unit || "u",
      ref_product_distrib: data.ref_product_distrib || "",
      breakdowns:          (data.qline_breakdown_blueprints || []).map(b => ({
        _clientId:           rid(),
        id:                  null,
        cost_category:       b.cost_category || "materials",
        description:         b.description || "",
        ref_product_distrib: b.ref_product_distrib || "",
        unit:                b.unit || (b.cost_category === "labor" ? "h" : "u"),
        quantity:            b.quantity || 0,
        unit_price:          b.unit_price || 0,
        purchase_price:      b.purchase_price || 0,
        product_id:          b.product?.id || null,
        product_name:        b.product?.name || "",
        _destroy:            false,
      })),
    }))
  }
  const chooseManualPrestation = clientId => update(clientId, { _pickMode: false, breakdowns: [blankBreakdown()] })

  const linkProduct = clientId => data => update(clientId, {
    product_id:          data.id,
    product_name:        data.name,
    description:         data.name || "",
    ref_product_distrib: data.ref_product_brand || "",
    unit:                data.unit || "u",
    purchase_price:      data.purchase_price ?? 0,
    unit_price:          data.sale_price ?? 0,
  })
  const unlinkProduct = clientId => update(clientId, { product_id: null, product_name: "" })

  const setBreakdowns = (clientId, fn) =>
    setLines(prev => prev.map(l => (l._clientId === clientId ? { ...l, breakdowns: fn(l.breakdowns || []) } : l)))
  const addBreakdown    = clientId            => setBreakdowns(clientId, bds => [...bds, blankBreakdown()])
  const updateBreakdown = (clientId, bid, patch) => setBreakdowns(clientId, bds => bds.map(b => (b._clientId === bid ? { ...b, ...patch } : b)))
  const removeBreakdown = (clientId, bid)     => setBreakdowns(clientId, bds => bds.map(b => {
    if (b._clientId !== bid) return b
    return b.id ? { ...b, _destroy: true } : null
  }).filter(Boolean))
  const linkBreakdownProduct = (clientId, bid) => data => updateBreakdown(clientId, bid, {
    product_id:          data.id,
    product_name:        data.name,
    description:         data.name || "",
    ref_product_distrib: data.ref_product_brand || "",
    unit:                data.unit || "u",
    purchase_price:      data.purchase_price ?? 0,
    unit_price:          data.sale_price ?? 0,
    ...(data.cost_category ? { cost_category: data.cost_category } : {}),
  })
  const unlinkBreakdownProduct = (clientId, bid) => updateBreakdown(clientId, bid, { product_id: null, product_name: "" })

  // Drag-to-reorder
  const containerRef = useRef(null)
  const sortableRef  = useRef(null)
  const reorder = (from, to) => setLines(prev => {
    const vis  = prev.filter(l => !l._destroy)
    const dead = prev.filter(l => l._destroy)
    const next = [...vis]
    const [moved] = next.splice(from, 1)
    next.splice(to, 0, moved)
    return [...next, ...dead]
  })
  const reorderRef = useRef(reorder); reorderRef.current = reorder
  useEffect(() => {
    if (!isOpen || loading || !containerRef.current) return
    sortableRef.current = new Sortable(containerRef.current, {
      handle: ".sortable-handle", draggable: ".line-card", animation: 150, ghostClass: "opacity-40",
      onEnd: e => {
        const from = e.oldDraggableIndex ?? e.oldIndex
        const to   = e.newDraggableIndex ?? e.newIndex
        if (from !== to) reorderRef.current(from, to)
      },
    })
    return () => { sortableRef.current?.destroy(); sortableRef.current = null }
  }, [isOpen, loading])

  const handleSave = () => {
    if (saving) return
    if (!title.trim()) { flashAlert(t("name_required")); return }
    setSaving(true)

    const body = {
      quotes_qline_group: {
        title, unit, observation,
        include_subtotal:        subtotal,
        title_as_subtotal_label: titleAsLabel,
        qline_blueprints_attributes: lines.map((line, index) => {
          const base = { id: line.id || undefined, order: index, _destroy: line._destroy ? "1" : undefined }

          if (line.kind === "prestation") {
            const r = breakdownRollup(line.breakdowns)
            return {
              ...base,
              description:    line.description,
              unit:           line.unit || "u",
              quantity:       1,
              unit_price:     r.total,
              purchase_price: r.cost,
              is_page_break:  false,
              is_option:      line.is_option,
              hide_in_pdf:    line.hide_in_pdf,
              product_id:     null,
              qline_breakdown_blueprints_attributes: line.breakdowns.map((b, bi) => ({
                id:                  b.id || undefined,
                cost_category:       b.cost_category,
                description:         b.description,
                ref_product_distrib: b.ref_product_distrib,
                unit:                b.unit,
                quantity:            b.quantity,
                unit_price:          b.unit_price,
                purchase_price:      b.purchase_price,
                product_id:          b.product_id || null,
                order:               bi,
                _destroy:            b._destroy ? "1" : undefined,
              })),
            }
          }

          return {
            ...base,
            description:         line.description,
            ref_product_distrib: line.kind === "regular" ? line.ref_product_distrib : "",
            unit:                line.kind === "regular" ? line.unit : "",
            quantity:            line.kind === "regular" ? line.quantity : 0,
            unit_price:          line.kind === "regular" ? line.unit_price : 0,
            purchase_price:      line.kind === "regular" ? line.purchase_price : 0,
            product_id:          line.kind === "regular" ? (line.product_id || null) : null,
            is_page_break:       line.kind === "page_break",
            is_option:           line.kind === "regular" ? line.is_option : false,
            hide_in_pdf:         line.kind === "regular" ? line.hide_in_pdf : false,
          }
        }),
      },
    }

    const request = mode === "edit"
      ? patchRequest({ url: updateUrl, body })
      : postRequest({ url: saveUrl, body })

    request
      .then(response => {
        if (response && response.success === false) {
          flashAlert(Array.isArray(response.errors) ? response.errors.join(", ") : t("save_error"))
          return
        }
        flashNotice(mode === "edit" ? t("saved") : t("created"))
        onSaved && onSaved()
        onClose()
      })
      .catch(() => flashAlert(t("network_error")))
      .finally(() => setSaving(false))
  }

  const footer = (
    <>
      <div />
      <div className="flex items-center gap-2">
        <Button variant="ghost" onClick={onClose}>{t("cancel")}</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          <FontAwesomeIcon icon={saving ? "spinner" : "check"} spin={saving} />
          {mode === "edit" ? t("save") : t("create")}
        </Button>
      </div>
    </>
  )

  return (
    <Modal
      isOpen  = {isOpen}
      onClose = {onClose}
      eyebrow = {mode === "edit" ? t("eyebrow_edit") : t("eyebrow_new")}
      title   = {mode === "edit" ? (title || t("title_edit")) : t("title_new")}
      footer  = {footer}
    >
      {loading && (
        <div className="py-12 text-center text-sm text-slate-500">
          <FontAwesomeIcon icon="spinner" spin /> {t("loading")}
        </div>
      )}

      {!loading && (
        <>
          {/* General fields */}
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-9">
              <label className="block text-xs font-medium text-slate-700 mb-1.5">
                {t("name_label")} <span className="text-rose-500">*</span>
              </label>
              <input value={title} onChange={e => setTitle(e.target.value)}
                     placeholder={t("name_placeholder")}
                     className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-200 focus:border-brand-400" />
            </div>
            <div className="col-span-3">
              <label className="block text-xs font-medium text-slate-700 mb-1.5">{t("unit_label")}</label>
              <input value={unit} onChange={e => setUnit(e.target.value)}
                     placeholder={t("unit_placeholder")}
                     className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-200 focus:border-brand-400" />
            </div>
            <div className="col-span-12">
              <label className="block text-xs font-medium text-slate-700 mb-1.5">
                {t("description_label")} <span className="text-slate-400 font-normal">{t("description_hint")}</span>
              </label>
              <textarea ref={obsRef} value={observation} onChange={e => setObservation(e.target.value)} rows="2"
                        placeholder={t("description_placeholder")}
                        className="w-full px-3 py-2 rounded-md border border-slate-200 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-200 focus:border-brand-400"
                        style={{ overflow: "hidden", resize: "none" }} />
            </div>
            <div className="col-span-12 flex flex-wrap gap-x-6 gap-y-2">
              <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" checked={subtotal} onChange={e => setSubtotal(e.target.checked)}
                       className="h-4 w-4 rounded border-slate-300 text-brand-500 focus:ring-brand-200" />
                {I18n.t("qline_groups.include_subtotal")}
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" checked={titleAsLabel} onChange={e => setTitleAsLabel(e.target.checked)}
                       className="h-4 w-4 rounded border-slate-300 text-brand-500 focus:ring-brand-200" />
                {I18n.t("qline_groups.title_as_subtotal_label")}
              </label>
            </div>
          </div>

          {/* Breakdown */}
          <div>
            <div className="mb-2">
              <h3 className="text-sm font-semibold text-slate-900">{t("breakdown")}</h3>
              <p className="text-xs text-slate-500 mt-0.5">{t("line_count", { count: visible.length })}</p>
            </div>

            <div className="border border-slate-200 rounded-lg overflow-visible">
              {/* Header */}
              <div className="grid items-center gap-x-2 px-1 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider bg-slate-50 border-b border-slate-200"
                   style={{ gridTemplateColumns: GRID }}>
                <div />
                <div />
                <div>{t("header_unit")}</div>
                <div className="text-right">{t("header_qty")}</div>
                <div className="text-right">{t("header_purchase")}</div>
                <div className="text-right">{t("header_sale")}</div>
                <div className="text-right">{t("header_coef")}</div>
                <div className="text-right">{t("header_margin")}</div>
                <div className="text-right">{t("header_total")}</div>
                <div />
                <div />
              </div>

              <div ref={containerRef}>
                {visible.map(line => {
                  if (line.kind === "page_break") {
                    return (
                      <div key={line._clientId} className="line-card border-t border-slate-100 first:border-t-0 flex items-center gap-2 px-1 py-2 bg-slate-50/40">
                        <span className="sortable-handle flex items-center justify-center h-9 w-7 cursor-grab text-slate-300 hover:text-slate-500" title={t("drag_handle")}>
                          <FontAwesomeIcon icon="grip-vertical" />
                        </span>
                        <div className="flex-1 flex items-center gap-2 text-xs uppercase tracking-wider text-slate-400">
                          <span className="flex-1 border-t border-dashed border-slate-300" />
                          <FontAwesomeIcon icon="file-lines" /> {t("page_break")}
                          <span className="flex-1 border-t border-dashed border-slate-300" />
                        </div>
                        <IconButton tone="danger" size="md" title={t("remove_line")} onClick={() => remove(line._clientId)}>
                          <FontAwesomeIcon icon="xmark" />
                        </IconButton>
                      </div>
                    )
                  }

                  if (line.kind === "descriptive") {
                    return (
                      <div key={line._clientId} className="line-card border-t border-slate-100 first:border-t-0 hover:bg-slate-50/40 flex items-start gap-2 px-1 py-2">
                        <span className="sortable-handle flex items-center justify-center h-9 w-7 cursor-grab text-slate-300 hover:text-slate-500" title={t("drag_handle")}>
                          <FontAwesomeIcon icon="grip-vertical" />
                        </span>
                        <div className="flex-1">
                          <div className="flex items-center gap-1.5 mb-1 text-[11px] font-medium uppercase tracking-wider text-slate-400">
                            <FontAwesomeIcon icon="align-left" /> {t("descriptive_badge")}
                          </div>
                          <QuillInput
                            text     = {line.description}
                            onChange = {content => update(line._clientId, { description: content })}
                          />
                        </div>
                        <IconButton tone="danger" size="md" title={t("remove_line")} onClick={() => remove(line._clientId)}>
                          <FontAwesomeIcon icon="xmark" />
                        </IconButton>
                      </div>
                    )
                  }

                  if (line.kind === "prestation" && line._pickMode) {
                    return (
                      <div key={line._clientId} className="line-card border-t border-slate-100 first:border-t-0">
                        <div className="flex items-start gap-2 px-1 py-2">
                          <span className="sortable-handle flex items-center justify-center h-9 w-7 cursor-grab text-slate-300 hover:text-slate-500" title={t("drag_handle")}>
                            <FontAwesomeIcon icon="grip-vertical" />
                          </span>
                          <div className="flex-1">
                            <div className="flex items-center gap-1.5 mb-1 text-[11px] font-medium uppercase tracking-wider text-brand-600">
                              <FontAwesomeIcon icon="screwdriver-wrench" /> {t("prestation_badge")}
                            </div>
                            <Combobox
                              options         = {prestationOptions}
                              onSelectElement = {opt => opt && seedFromPrestation(line._clientId, opt.id)}
                              prompt          = {t("prestation_select")}
                            />
                            <button type="button" onClick={() => chooseManualPrestation(line._clientId)}
                                    className="mt-2 text-xs text-slate-500 hover:text-brand-600">
                              <FontAwesomeIcon icon="pen" className="text-[10px]" /> {t("prestation_manual")}
                            </button>
                          </div>
                          <IconButton tone="danger" size="md" title={t("remove_line")} onClick={() => remove(line._clientId)}>
                            <FontAwesomeIcon icon="xmark" />
                          </IconButton>
                        </div>
                      </div>
                    )
                  }

                  if (line.kind === "prestation") {
                    const r   = breakdownRollup(line.breakdowns)
                    const bds = (line.breakdowns || []).filter(b => !b._destroy)
                    return (
                      <div key={line._clientId} className="line-card border-t border-slate-100 first:border-t-0 hover:bg-slate-50/40">
                        <div className="flex items-start gap-2 px-1 pt-2">
                          <span className="sortable-handle flex items-center justify-center h-9 w-7 cursor-grab text-slate-300 hover:text-slate-500" title={t("drag_handle")}>
                            <FontAwesomeIcon icon="grip-vertical" />
                          </span>
                          <div className="flex-1">
                            <div className="flex items-center gap-1.5 mb-1 text-[11px] font-medium uppercase tracking-wider text-brand-600">
                              <FontAwesomeIcon icon="screwdriver-wrench" /> {t("prestation_badge")}
                            </div>
                            <input value={line.description} onChange={e => update(line._clientId, { description: e.target.value })}
                                   placeholder={t("prestation_placeholder")} className={`${CELL} font-medium`} />
                          </div>
                          <LineOptions line={line} onChange={patch => update(line._clientId, patch)} />
                          <IconButton tone="danger" size="md" title={t("remove_line")} onClick={() => remove(line._clientId)}>
                            <FontAwesomeIcon icon="xmark" />
                          </IconButton>
                        </div>

                        <div className="mx-1 mb-2 mt-1 border border-slate-200 rounded-lg overflow-visible">
                          <div className="grid items-center gap-x-2 px-1 py-2 text-[11px] font-medium text-slate-500 uppercase tracking-wider bg-slate-50 border-b border-slate-200"
                               style={{ gridTemplateColumns: BD_GRID }}>
                            <div>{I18n.t("quotes.qline_service_blueprints.form.line_header_ref")}</div>
                            <div>{I18n.t("quotes.qline_service_blueprints.form.line_header_category")}</div>
                            <div>{I18n.t("quotes.qline_service_blueprints.form.line_header_unit")}</div>
                            <div className="text-right">{I18n.t("quotes.qline_service_blueprints.form.line_header_qty")}</div>
                            <div className="text-right">{I18n.t("quotes.qline_service_blueprints.form.line_header_purchase")}</div>
                            <div className="text-right">{I18n.t("quotes.qline_service_blueprints.form.line_header_sale")}</div>
                            <div className="text-right">{I18n.t("quotes.qline_service_blueprints.form.line_header_coef")}</div>
                            <div className="text-right">{I18n.t("quotes.qline_service_blueprints.form.line_header_margin")}</div>
                            <div className="text-right">{I18n.t("quotes.qline_service_blueprints.form.line_header_total")}</div>
                            <div />
                          </div>

                          {bds.map(b => {
                            const bq = parseFloat(b.quantity) || 0
                            const bs = parseFloat(b.unit_price) || 0
                            const bp = parseFloat(b.purchase_price) || 0
                            const bTotal  = bq * bs
                            const bCoef   = bp > 0 ? bs / bp : null
                            const bMar    = bs > 0 ? ((bs - bp) / bs) * 100 : 0
                            const bLocked = !!b.product_id
                            return (
                              <div key={b._clientId} className="border-t border-slate-100 first:border-t-0">
                                <div className="flex items-center gap-2 px-1 pt-2">
                                  <input value={b.description} onChange={e => updateBreakdown(line._clientId, b._clientId, { description: e.target.value })}
                                         placeholder={I18n.t("quotes.qline_service_blueprints.form.line_label_placeholder")} className={`${CELL} flex-1 font-medium`} />
                                  <div className="w-56 shrink-0">
                                    {b.product_id ? (
                                      <div className="flex items-center gap-1.5 h-9 px-2 rounded border border-emerald-200 bg-emerald-50 text-xs text-emerald-800">
                                        <FontAwesomeIcon icon="box" className="text-emerald-500" />
                                        <span className="flex-1 truncate" title={b.product_name}>{b.product_name}</span>
                                        <button type="button" onClick={() => unlinkBreakdownProduct(line._clientId, b._clientId)} className="text-emerald-600 hover:text-emerald-900">
                                          <FontAwesomeIcon icon="xmark" />
                                        </button>
                                      </div>
                                    ) : (
                                      <ProductPicker placeholder={I18n.t("quotes.qline_service_blueprints.form.product_search_placeholder")} onSelect={linkBreakdownProduct(line._clientId, b._clientId)} />
                                    )}
                                  </div>
                                </div>
                                <div className="grid items-center gap-x-2 px-1 pb-2 pt-1" style={{ gridTemplateColumns: BD_GRID }}>
                                  <input value={b.ref_product_distrib} maxLength={30}
                                         onChange={e => updateBreakdown(line._clientId, b._clientId, { ref_product_distrib: e.target.value })}
                                         placeholder={I18n.t("quotes.qline_service_blueprints.form.line_ref_placeholder")} className={`${CELL} font-mono`} />
                                  <select value={b.cost_category}
                                          onChange={e => { const c = e.target.value; updateBreakdown(line._clientId, b._clientId, { cost_category: c, ...(c === "labor" ? { unit: "h" } : {}) }) }}
                                          className={`${CELL} bg-white`}>
                                    {COST_CATEGORIES.map(c => <option key={c} value={c}>{I18n.t(`quotes.new.cost_category.${c}`)}</option>)}
                                  </select>
                                  <input value={b.unit} disabled={b.cost_category === "labor"}
                                         onChange={e => updateBreakdown(line._clientId, b._clientId, { unit: e.target.value })}
                                         className={`${CELL} ${b.cost_category === "labor" ? "bg-slate-50 text-slate-500 cursor-not-allowed" : ""}`} />
                                  <input type="number" step="0.01" value={b.quantity}
                                         onChange={e => updateBreakdown(line._clientId, b._clientId, { quantity: e.target.value })}
                                         className={`${CELL} text-right tabular-nums`} />
                                  <input type="number" step="0.01" value={b.purchase_price} disabled={bLocked}
                                         onChange={e => updateBreakdown(line._clientId, b._clientId, { purchase_price: e.target.value })}
                                         className={`${CELL} text-right tabular-nums ${bLocked ? "bg-slate-50 text-slate-500 cursor-not-allowed" : ""}`} />
                                  <input type="number" step="0.01" value={b.unit_price} disabled={bLocked}
                                         onChange={e => updateBreakdown(line._clientId, b._clientId, { unit_price: e.target.value })}
                                         className={`${CELL} text-right tabular-nums ${bLocked ? "bg-slate-50 text-slate-500 cursor-not-allowed" : ""}`} />
                                  <div className="px-2 text-right tabular-nums text-sm text-slate-700">{coefFmt(bCoef)}</div>
                                  <div className="text-right"><MarginBadge pct={bMar} /></div>
                                  <div className="px-2 text-right tabular-nums text-sm font-semibold text-slate-900">{money(bTotal)}</div>
                                  <IconButton tone="danger" size="md" title={I18n.t("quotes.qline_service_blueprints.form.remove_line")} onClick={() => removeBreakdown(line._clientId, b._clientId)}>
                                    <FontAwesomeIcon icon="xmark" />
                                  </IconButton>
                                </div>
                              </div>
                            )
                          })}

                          <div className="border-t border-slate-200">
                            <button type="button" onClick={() => addBreakdown(line._clientId)}
                                    className="w-full py-2 text-sm text-slate-500 hover:text-brand-600 hover:bg-brand-50 flex items-center justify-center gap-2">
                              <FontAwesomeIcon icon="plus" className="text-xs" /> {I18n.t("quotes.qline_service_blueprints.form.add_another_line")}
                            </button>
                          </div>
                        </div>

                        <div className="flex justify-end gap-6 px-2 pb-2 text-xs text-slate-500">
                          <span>{t("sale_excl_tax")} <span className="tabular-nums font-semibold text-slate-800">{money(r.total)}</span></span>
                          <span>{t("header_coef")} <span className="tabular-nums font-semibold text-slate-800">{coefFmt(r.coef)}</span></span>
                          <span>{t("margin")} <span className="tabular-nums font-semibold text-slate-800">{r.marginPct.toFixed(1)}%</span></span>
                        </div>
                      </div>
                    )
                  }

                  // regular
                  const qty      = parseFloat(line.quantity)       || 0
                  const sale     = parseFloat(line.unit_price)     || 0
                  const purchase = parseFloat(line.purchase_price) || 0
                  const lineTotal  = qty * sale
                  const lineCoef   = purchase > 0 ? sale / purchase : null
                  const lineMarPct = sale > 0 ? ((sale - purchase) / sale) * 100 : 0
                  const locked     = !!line.product_id

                  return (
                    <div key={line._clientId} className="line-card border-t border-slate-100 first:border-t-0 hover:bg-slate-50/40">
                      <div className="flex items-start gap-2 px-1 pt-2">
                        <span className="w-7 shrink-0" aria-hidden="true" />
                        <div className="flex-1">
                          <QuillInput
                            text     = {line.description}
                            onChange = {content => update(line._clientId, { description: content })}
                          />
                        </div>
                        <div className="w-64 shrink-0">
                          {line.product_id ? (
                            <div className="flex items-center gap-1.5 h-9 px-2 rounded border border-emerald-200 bg-emerald-50 text-xs text-emerald-800">
                              <FontAwesomeIcon icon="box" className="text-emerald-500" />
                              <span className="flex-1 truncate" title={`${line.product_name} — ${t("product_linked")}`}>{line.product_name}</span>
                              <button type="button" onClick={() => unlinkProduct(line._clientId)} title={t("product_unlink")} className="text-emerald-600 hover:text-emerald-900">
                                <FontAwesomeIcon icon="xmark" />
                              </button>
                            </div>
                          ) : (
                            <ProductPicker placeholder={t("product_search_placeholder")} onSelect={linkProduct(line._clientId)} />
                          )}
                        </div>
                      </div>
                      <div className="grid items-center gap-x-2 px-1 pb-2 pt-1" style={{ gridTemplateColumns: GRID }}>
                        <span className="sortable-handle flex items-center justify-center h-9 w-full cursor-grab text-slate-300 hover:text-slate-500" title={t("drag_handle")}>
                          <FontAwesomeIcon icon="grip-vertical" />
                        </span>

                        <div aria-hidden="true" />

                        <input value={line.unit} onChange={e => update(line._clientId, { unit: e.target.value })}
                               placeholder={t("unit_placeholder")} className={CELL} />

                        <input type="number" step="0.01" value={line.quantity}
                               onChange={e => update(line._clientId, { quantity: e.target.value })}
                               className={`${CELL} text-right tabular-nums`} />

                        <input type="number" step="0.01" value={line.purchase_price} disabled={locked}
                               title={locked ? t("product_linked") : undefined}
                               onChange={e => update(line._clientId, { purchase_price: e.target.value })}
                               className={`${CELL} text-right tabular-nums ${locked ? "bg-slate-50 text-slate-500 cursor-not-allowed" : ""}`} />

                        <input type="number" step="0.01" value={line.unit_price} disabled={locked}
                               title={locked ? t("product_linked") : undefined}
                               onChange={e => update(line._clientId, { unit_price: e.target.value })}
                               className={`${CELL} text-right tabular-nums ${locked ? "bg-slate-50 text-slate-500 cursor-not-allowed" : ""}`} />

                        <div className="px-2 text-right tabular-nums text-sm text-slate-700">{coefFmt(lineCoef)}</div>
                        <div className="text-right"><MarginBadge pct={lineMarPct} /></div>
                        <div className="px-2 text-right tabular-nums text-sm font-semibold text-slate-900">{money(lineTotal)}</div>

                        <LineOptions line={line} onChange={patch => update(line._clientId, patch)} />

                        <IconButton tone="danger" size="md" title={t("remove_line")} onClick={() => remove(line._clientId)}>
                          <FontAwesomeIcon icon="xmark" />
                        </IconButton>
                      </div>
                    </div>
                  )
                })}

                {visible.length === 0 && (
                  <div className="py-10 text-center text-sm text-slate-500">{t("empty_lines")}</div>
                )}
              </div>

              <div className="border-t border-slate-200 p-2 flex items-center justify-center gap-2">
                <Button variant="secondary" size="sm" onClick={() => addLine("prestation")}>
                  <FontAwesomeIcon icon="screwdriver-wrench" /> {t("add_prestation")}
                </Button>
                <Button variant="secondary" size="sm" onClick={() => addLine("descriptive")}>
                  <FontAwesomeIcon icon="align-left" /> {t("add_descriptive")}
                </Button>
                <Button variant="secondary" size="sm" onClick={() => addLine("page_break")}>
                  <FontAwesomeIcon icon="file-lines" /> {t("add_page_break")}
                </Button>
                <Button variant="primary" size="sm" onClick={() => addLine("regular")}>
                  <FontAwesomeIcon icon="plus" /> {t("add_line")}
                </Button>
              </div>
            </div>

            {/* Totals */}
            <div className="flex justify-end mt-4">
              <div className="w-[420px] bg-slate-50 rounded-lg p-4 border border-slate-200">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">{t("totals_heading")}</div>
                <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                  <span className="text-slate-600">{t("sale_excl_tax")}</span>
                  <span className="tabular-nums font-semibold text-slate-900 text-right">{money(rollup.total)}</span>
                  <span className="text-slate-600">{t("purchase_cost")}</span>
                  <span className="tabular-nums text-slate-700 text-right">{money(rollup.cost)}</span>
                  <span className="text-slate-600">{t("margin")}</span>
                  <span className="tabular-nums text-slate-900 font-medium text-right">{money(rollup.margin)}</span>
                </div>
                <div className="h-px bg-slate-200 my-3" />
                <div className="grid grid-cols-2 gap-x-6 text-sm">
                  <div>
                    <div className="text-xs text-slate-500">{t("global_coef")}</div>
                    <div className="text-lg font-bold tabular-nums text-slate-900">{coefFmt(rollup.coef)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-slate-500">{t("global_margin")}</div>
                    <div className="text-lg font-bold tabular-nums text-slate-900">{rollup.marginPct.toFixed(1)}%</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </Modal>
  )
}

export default QlineGroupFormModal
